import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  Link,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridRowEditStopReasons,
  GridRowModes,
} from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import { jwtDecode } from "jwt-decode";

function generateColorFromId(id) {
  const hue = (id * 137) % 360;
  return `hsl(${hue}, 70%, 80%)`;
}

const Shops = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(null);

  const columns = [
    { field: "id", headerName: "ลำดับ", flex: 0, width: 70 },
    {
      field: "username",
      headerName: "ชื่อผู้ใช้งาน",
      flex: 1,
      editable: false,
    },
    { field: "name", headerName: "ชื่อ", flex: 1, editable: true },
    { field: "lastname", headerName: "นามสกุล", flex: 1, editable: true },
    {
      field: "branch",
      headerName: "สาขา",
      width: 200,
      display: "flex",
      alignContent: "center",
      flex: 1,
      renderCell: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return (
            <FormControl sx={{ width: "100%" }}>
              <Select
                size="small"
                value={row?.branchId || ""}
                onChange={(event) =>
                  handleBranchChange(row.id, event.target.value)
                }
                // renderValue={(selected) => {
                //     const selectedItem = branches.find((branch) => branch.value === selected);
                //     return selectedItem ? <Chip label={selectedItem.label} /> : null;
                //   }}
              >
                {branches.map((option) => (
                  <MenuItem key={option.key} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        return row.branchId ? (
          <Chip
            sx={{
              background: generateColorFromId(row.branchId), // สร้างสีจาก branchId
            }}
            label={
              branches.find((branch) => branch.value === row.branchId)?.label
            }
          />
        ) : null;
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-save`}
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-cancel`}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            key={`${id}-delete`}
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  const handleEditClick = (id) => () => {
    const user = rows.find((row) => row.id === id);
    navigate(`/users/edit/${user.userId}`);
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);
    // // console.log("deletedRow :",deletedRow)

    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteUser(deletedRow.userId, shopId)
          .then((response) => {
            console.log("User deleted successfully:", response);
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: {
                container: "my-swal",
              },
              title: "ลบแล้ว!",
              text: `${deletedRow.name} ${deletedRow.lastname} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows
                .filter((row) => row.branchId !== deletedRow.branchId)
                .map((item, index) => ({ ...item, id: index + 1 }))
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting user:", error);
          });
      }
    });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }

    const originalRow = revertData(editedRow);
    return originalRow; // คืนค่าผลลัพธ์เดิม
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const payload = {
      name: newRow.name,
      lastname: newRow.lastname,
      branchId: newRow.branchId,
    };

    const updatedPayload = {
      ...payload,
      branchId: payload.branchId === "" ? null : payload.branchId,
    };

    console.log("updatedPayload", updatedPayload);
    services
      .updateUser(newRow.userId, updatedPayload, shopId)
      .then((response) => {
        toast.success("แก้ไขสำเร็จ!");
        console.log("User updated successfully:", response);
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      })
      .catch((error) => {
        toast.error("แก้ไขไม่สำเร็จ!");
        console.error("Error creating user:", error);
      });

    const originalRow = revertData(newRow);
    return originalRow; // คืนค่าผลลัพธ์เดิม
  };

  const revertData = (newRow) => {
    const originalRow = rows.find((row) => row.id === newRow.id);
    originalRow.branchId = originalRow.branchId_old || originalRow.branchId;

    if (originalRow) {
      setRows(rows.map((row) => (row.id === newRow.id ? originalRow : row)));
    }

    setRowModesModel({
      ...rowModesModel,
      [newRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    console.log("Cancel the edit and revert to the original data");
    return originalRow;
  };

  const handleBranchChange = (id, value) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id
          ? { ...row, branchId: value, branchId_old: row.branchId }
          : row
      )
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!shopId) return;

        const shopResponse = await services.getShop(shopId);
        const shopData = shopResponse.data.data;
        // console.log("Shop data fetched:", shopData);

        const usersWithIndex = shopData.users.map((item, index) => ({
          ...item,
          id: index + 1,
          userId: item.id,
          branch: shopData.branches.find(
            (branch) => branch?.id === item.branchId
          )?.name,
        }));

        const updatedBranches = shopData.branches.map((branch, index) => ({
          ...branch,
          key: `branch-${index}`,
          value: branch?.id,
          label: branch?.name,
        }));

        // console.log("updatedBranches",updatedBranches)
        setBranches(updatedBranches);
        setRows(usersWithIndex);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    const accessToken = localStorage.getItem("accessToken");
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.id) {
          setShopId(decoded.id); // อัพเดตค่า shopId ผ่าน state
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    fetchData();
  }, [shopId]);

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 2, mb: 2 }}>
        <Link underline="hover" color="inherit" href="/">
          หน้าแรก
        </Link>
        <Typography color="text.primary">จัดการพนักงาน</Typography>
      </Breadcrumbs>

      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          จัดการพนักงาน
        </Typography>

        <Button
          color="success"
          variant="contained"
          onClick={() => navigate("/users/create")}
        >
          เพิ่มพนักงาน
        </Button>
      </Box>
      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={rows}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              loading={loading}
              slotProps={{
                toolbar: { setRows, setRowModesModel },
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{ pagination: { page: 0, pageSize: 50 } }}
              pageSizeOptions={[25, 50, 100]}
              // getRowId={(row) => row.id}
              // checkboxSelection
              // onRowSelectionModelChange={(newRowSelectionModel) => {
              //   setRowSelectionModel(newRowSelectionModel);
              // }}
            />
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Shops;

// import React, { useState } from "react";
// import { DataGrid } from "@mui/x-data-grid";
// import { TextField, Select, MenuItem, FormControl } from "@mui/material";

// const DataGridWithInput = () => {
//   const [rows, setRows] = useState([
//     { id: 1, name: "John Doe", role: "Admin" },
//     { id: 2, name: "Jane Smith", role: "" },
//   ]);

//   const handleRoleChange = (id, value) => {
//     setRows((prevRows) =>
//       prevRows.map((row) => (row.id === id ? { ...row, role: value } : row))
//     );
//   };

//   const columns = [
//     { field: "id", headerName: "ID", width: 90 },
//     { field: "name", headerName: "Name", width: 150 },
//     {
//       field: "role",
//       headerName: "Role",
//       width: 200,
//       display: "flex",
//       alignContent: "center",
//       renderCell: (params) => (
//         <FormControl sx={{ width: "100%" }}>
//           <Select
//             size="small"
//             value={params.row.role}
//             onChange={(event) =>
//               handleRoleChange(params.row.id, event.target.value)
//             }
//           >
//             <MenuItem value="Admin">Admin</MenuItem>
//             <MenuItem value="Editor">Editor</MenuItem>
//             <MenuItem value="Viewer">Viewer</MenuItem>
//           </Select>
//         </FormControl>
//       ),
//     },
//   ];

//   return (
//     <div style={{ height: 400, width: "100%" }}>
//       <DataGrid
//         rows={rows}
//         columns={columns}
//         pageSize={5}
//         rowsPerPageOptions={[5]}
//         disableSelectionOnClick
//       />
//     </div>
//   );
// };

// export default DataGridWithInput;
